/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile, DbModuleFile, DbTenderSVFAttachment } from '~src/types/db';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { SVFViewer } from '~components/atoms/Viewers/SVFViewer/SVFViewer';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { Progress } from 'antd';
import {
  useSVFViewer,
  ViewableInfo,
} from '~src/hooks/svf-viewer-hooks/useSVFViewer';
import { useProjectId } from '~src/hooks/useProjectId';
import { debugLog } from '~src/utils/log';
import ViewSelector from '~components/atoms/Viewers/SVFViewer/ViewSelector';
import {
  ClockCircleOutlined,
  CheckCircleTwoTone,
  LoadingOutlined,
} from '@ant-design/icons';

interface SVFViewerProgressProps {
  fileToView?: DbFile | DbModuleFile | DbTenderSVFAttachment | undefined;
  onSuccess: () => void;
  onClose: () => void;
  justTranscribed?: boolean;
  setJustTranscribed?: (value: boolean) => void;
  // ==modal local state sync
  // Used by attachment-based sources that do not update through FileVersionsContext.
  onStateChange?: (nextState: number) => void;

  // ==parent state sync
  // Used by root tender attachments so RightSideView can keep table icons,
  // modal reopen state and background polling in sync.
  onAttachmentStateChange?: (nextState: number) => void;
}

const isInProgress = (state?: number) => [1, 2, 4].includes(state || 0);
const isSuccess = (state?: number) => state === 7;
const isFailed = (state?: number) => state === 5;

type TranscribeTimeline = {
  created_at?: number | null;
  download_source_file_end_at?: number | null;
  upload_source_file_end_at?: number | null;
  translate_svf_end_at?: number | null;
};

/** Stage timeline func */
const StageRow: React.FC<{
  done: boolean;
  label: string;
  isActive?: boolean;
  activeLabel?: string;
}> = ({ done, label, isActive = false, activeLabel }) => {
  const styles = useStyles(styleFn);
  const displayLabel = isActive && activeLabel ? activeLabel : label;

  return (
    <div css={styles.stageRow}>
      {done ? (
        <CheckCircleTwoTone twoToneColor="#52c41a" />
      ) : isActive ? (
        <LoadingOutlined style={{ color: '#1890ff' }} />
      ) : (
        <ClockCircleOutlined />
      )}
      <span css={isActive ? styles.activeStageLabel : undefined}>
        {displayLabel}
      </span>
    </div>
  );
};

export const Transcribe = ({
  fileToView,
  onSuccess,
  setJustTranscribed,
  onStateChange,
  onAttachmentStateChange,
}: SVFViewerProgressProps) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const {
    transcribe,
    checkTranscribeStatus,
    status,
    setStatus,
    stopCheck,
    version,
  } = useSVFViewer(fileToView);
  const { fileVersionsContext } = useContext(FileVersionsContext);

  const runTranscribe = async () => {
    try {
      if (!version) {
        setStatus({
          loading: false,
          error: true,
          message: intl.formatMessage({ id: 'svf-version-invalid' }),
          progress: 0,
        });
        return null;
      }

      // Mark that transcribe button was just pressed
      if (setJustTranscribed) {
        setJustTranscribed(true);
      }

      setStatus({
        loading: true,
        error: false,
        message: intl.formatMessage({ id: 'svf-transcribing' }),
        progress: 0,
      });

      await transcribe();

      // ==attachment source bridge
      // Root tender attachments do not refresh through FileVersionsContext,
      // so right after transcribe starts we have to:
      // 1) switch this modal into Progress mode
      // 2) sync the parent RightSideView state, so root table icons update too
      onStateChange?.(1);
      onAttachmentStateChange?.(1);

      // Let TranscribeProgress own the live polling loop,
      // so cleanup stays in one place when modal closes
    } catch (error) {
      setStatus({
        loading: false,
        error: true,
        message: intl.formatMessage({ id: 'svf-view-failed-open' }),
        progress: 0,
      });
    }
  };

  useEffect(() => {
    return () => {
      stopCheck();
    };
  }, []);

  if (status.error || isFailed(version?.extension_svf_model?.state)) {
    return (
      <div css={styles.messageContainer}>
        <p css={styles.errorMessage}>{status.message}</p>
        <Button type="primary" onClick={() => runTranscribe()}>
          {intl.formatMessage({ id: 'retry-transcribe' })}
        </Button>
      </div>
    );
  }
  if (status.loading) {
    return (
      <div css={styles.messageContainer}>
        <Progress type="circle" percent={status.progress} />
        <p css={styles.infoMessage}>{status.message}</p>
      </div>
    );
  }

  return (
    <div css={styles.messageContainer}>
      <p css={styles.infoMessage}>
        {intl.formatMessage({ id: 'svf-view-file-convert' })}
      </p>
      <Button type="primary" onClick={() => runTranscribe()}>
        {intl.formatMessage({ id: 'transcribe' })}
      </Button>
    </div>
  );
};

export const TranscribeProgress = ({
  fileToView,
  onSuccess,
  onClose,
  justTranscribed = false,
  setJustTranscribed,
  onStateChange,
  onAttachmentStateChange,
}: SVFViewerProgressProps) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const { checkTranscribeStatus, status, stopCheck } = useSVFViewer(fileToView);

  /** last transcribe check statuses */
  const [timeline, setTimeline] = useState<TranscribeTimeline | null>(null);

  useEffect(() => {
    let mounted = true;

    // Apply delay only if transcribe button was just pressed
    const initialDelay = justTranscribed ? 1000 : 0;

    // Reset the flag since we're now handling the polling
    if (setJustTranscribed) {
      setJustTranscribed(false);
    }

    debugLog('SVF', 'TranscribeProgress determining delay', {
      justTranscribed,
      statusLoading: status.loading,
      initialDelay,
    });

    checkTranscribeStatus((data) => {
      if (!mounted) return;

      // ==live sync for attachment-based sources
      // Modal local state controls which screen is rendered (progress/success),
      // and parent sync keeps RightSideView/root table state updated in parallel.
      onStateChange?.(data.svf_model_state);
      onAttachmentStateChange?.(data.svf_model_state);

      // Transcribe Progress's statuses
      setTimeline((prev) => ({
        created_at: data.created_at ?? prev?.created_at ?? null,
        download_source_file_end_at:
          data.download_source_file_end_at ??
          prev?.download_source_file_end_at ??
          null,
        upload_source_file_end_at:
          data.upload_source_file_end_at ??
          prev?.upload_source_file_end_at ??
          null,
        translate_svf_end_at:
          data.translate_svf_end_at ?? prev?.translate_svf_end_at ?? null,
      }));

      if (data.svf_model_state === 7) {
        onSuccess();
      }
    }, initialDelay);

    return () => {
      mounted = false;
      stopCheck();
    };
  }, [fileToView?.id]);

  // Check if we're waiting for Autodesk (upload done but no real conversion progress yet)
  const isWaitingForAutodesk =
    Boolean(timeline?.upload_source_file_end_at) &&
    !Boolean(timeline?.translate_svf_end_at) &&
    status.message.includes('Waiting');

  // Check if Autodesk is actively converting (we're getting real progress)
  const isAutodeskConverting =
    Boolean(timeline?.upload_source_file_end_at) &&
    !Boolean(timeline?.translate_svf_end_at) &&
    !status.message.includes('Waiting') &&
    !status.message.includes('Finalizing') &&
    status.progress > 20;

  // Check if we're in finalizing stage
  const isFinalizing =
    Boolean(timeline?.upload_source_file_end_at) &&
    !Boolean(timeline?.translate_svf_end_at) &&
    status.message.includes('Finalizing');

  return (
    <div css={styles.messageContainer}>
      <Progress type="circle" percent={status.progress} />
      <p
        css={
          isWaitingForAutodesk || isFinalizing
            ? styles.pulsingMessage
            : styles.infoMessage
        }>
        {status.message}
      </p>
      {isWaitingForAutodesk && (
        <p css={styles.note}>
          {intl.formatMessage({
            id: 'svf-autodesk-processing',
            defaultMessage: 'Autodesk is processing your file',
          })}
        </p>
      )}
      {/* Stages timeline */}
      <div css={styles.stages}>
        <StageRow
          done={Boolean(timeline?.created_at)}
          isActive={!Boolean(timeline?.created_at)}
          label={intl.formatMessage({
            id: 'svf-stage-created',
            defaultMessage: 'Conversion initiated',
          })}
        />
        <StageRow
          done={Boolean(timeline?.download_source_file_end_at)}
          isActive={
            Boolean(timeline?.created_at) &&
            !Boolean(timeline?.download_source_file_end_at)
          }
          label={intl.formatMessage({
            id: 'svf-stage-download',
            defaultMessage: 'Download file from project',
          })}
        />
        <StageRow
          done={Boolean(timeline?.upload_source_file_end_at)}
          isActive={
            Boolean(timeline?.download_source_file_end_at) &&
            !Boolean(timeline?.upload_source_file_end_at)
          }
          label={intl.formatMessage({
            id: 'svf-stage-upload',
            defaultMessage: 'Transfer file to Autodesk',
          })}
        />
        <StageRow
          done={Boolean(timeline?.translate_svf_end_at)}
          isActive={
            isWaitingForAutodesk || isAutodeskConverting || isFinalizing
          }
          label={intl.formatMessage({
            id: 'svf-stage-done',
            defaultMessage: 'Conversion completed',
          })}
          activeLabel={
            isWaitingForAutodesk
              ? intl.formatMessage({
                  id: 'svf-waiting-for-autodesk',
                  defaultMessage: 'Waiting for Autodesk response',
                })
              : isFinalizing
              ? intl.formatMessage({
                  id: 'svf-finalizing',
                  defaultMessage: 'Finalizing conversion...',
                })
              : isAutodeskConverting
              ? intl.formatMessage({
                  id: 'svf-autodesk-converting',
                  defaultMessage: 'Autodesk is converting your file',
                })
              : undefined
          }
        />
      </div>
      <Button onClick={onClose}>
        {intl.formatMessage({
          id: 'close-window',
        })}
      </Button>
      {/* Notes */}
      <p css={styles.note}>
        {intl.formatMessage({
          id: 'svf-continues-when-closed',
        })}
      </p>
    </div>
  );
};

/** Success screen w/btns */
const SuccessView: React.FC<{
  onOpenWindowed?: () => void;
  onOpenFullscreen?: () => void;
  onClose: () => void;
}> = ({ onOpenWindowed, onOpenFullscreen, onClose }) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  return (
    <div css={styles.successView}>
      <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 48 }} />
      <p css={styles.successTitle}>
        {intl.formatMessage({
          id: 'svf-success',
        })}
      </p>
      <div css={styles.successActionsWrapper}>
        <Button type="primary" onClick={onOpenWindowed}>
          {intl.formatMessage({ id: 'open-window' })}
        </Button>
        <Button type="primary" onClick={onOpenFullscreen}>
          {intl.formatMessage({ id: 'open-fullscreen' })}
        </Button>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: 'close-window' })}
        </Button>
      </div>
    </div>
  );
};

export interface SVFViewerModalProps extends Partial<ModalProps> {
  fileToView?: DbFile | DbModuleFile | DbTenderSVFAttachment | undefined;
  onReloadData: (close: boolean) => void;
  forceViewer?: boolean;
  onOpenWindowed?: () => void;
  onOpenFullscreen?: () => void;

  // ==external attachment state bridge
  // RightSideView uses this to keep root tender attachment SVF state in sync
  // with table icons and background polling.
  onAttachmentStateChange?: (fileGuid: string, nextState: number) => void;
}

export const SVFViewerModal: React.FC<SVFViewerModalProps> = ({
  fileToView,
  onCancel,
  onReloadData,
  forceViewer,
  onOpenWindowed,
  onOpenFullscreen,
  onAttachmentStateChange,
  ...modalProps
}) => {
  const intl = useIntl();

  const { fileVersionsContext } = useContext(FileVersionsContext);
  const styles = useStyles(styleFn);

  // ==viewer header state
  // Used only when fullscreen SVF viewer is shown.
  const [headerViewables, setHeaderViewables] = useState<ViewableInfo[]>([]);
  const [activeGuid, setActiveGuid] = useState<string | undefined>(undefined);

  // ==modal display state
  const [fileName, setFileName] = useState('');
  const [justTranscribed, setJustTranscribed] = useState(false);

  // ==attachment source state
  // Root tender attachments do not live in FileVersionsContext,
  // so the modal keeps their current SVF state locally.
  const [localSVFState, setLocalSVFState] = useState<number | undefined>(
    undefined
  );

  // ==source detection
  // If SVF gets implemented for another non-versioned source later,
  // extend this branch and decide whether it should also use localSVFState.
  const isTenderAttachment =
    !!fileToView && (fileToView as any).sourceType === 'tender-admin-root-svf';

  const version =
    fileToView && !isTenderAttachment
      ? fileVersionsContext[fileToView.id]
      : null;

  const vState = isTenderAttachment
    ? localSVFState ?? (fileToView as any)?.extension_svf_model?.state
    : version?.extension_svf_model?.state;

  useEffect(() => {
    const fn = isTenderAttachment
      ? (fileToView as any)?.title ||
        (fileToView as any)?.file_name ||
        (fileToView as any)?.name ||
        ''
      : version != null
      ? `${version.name} (version ${version.revision})`
      : '';

    setFileName(fn);

    // ==reset local attachment state on source change
    // Regular versioned files continue to read their state from FileVersionsContext.
    setLocalSVFState(
      isTenderAttachment
        ? (fileToView as any)?.extension_svf_model?.state
        : undefined
    );

    setHeaderViewables([]);
    setActiveGuid(undefined);
  }, [fileToView?.id, isTenderAttachment, version]);

  const derived = useMemo(() => {
    const success = isSuccess(vState);
    return {
      showViewer: !!forceViewer,
      showSuccess: success,
      showProgress: isInProgress(vState) && !success,
      showTranscribe: !forceViewer && !success && !isInProgress(vState),
    };
  }, [vState, forceViewer]);

  const handleLocalSVFStateChange = (nextState: number) => {
    setLocalSVFState(nextState);
  };

  const handleAttachmentBridgeStateChange = (nextState: number) => {
    if (isTenderAttachment && fileToView?.id) {
      onAttachmentStateChange?.(String((fileToView as any).id), nextState);
    }
  };

  const handleModalCancel = () => {
    setActiveGuid(undefined);
    setHeaderViewables([]);
    if (derived.showViewer) {
      onCancel?.({} as any);
    } else {
      onReloadData(true);
    }
  };

  return (
    <Modal
      title={
        <div css={styles.titleContainer}>
          <ModalTitle
            text={`${intl.formatMessage({ id: 'view-file' })} - ${fileName}`}
            icon={['far', 'cube']}
          />
          {derived.showViewer && (
            <ViewSelector
              viewables={headerViewables}
              activeGuid={activeGuid ?? headerViewables[0]?.guid}
              onChange={(guid) => setActiveGuid(guid)}
            />
          )}
        </div>
      }
      centered
      destroyOnClose
      cancelText={intl.formatMessage({ id: 'close' })}
      onCancel={handleModalCancel}
      width={derived.showViewer ? '100%' : 500}
      css={
        derived.showViewer
          ? [styles.fullModalClassName]
          : [styles.transcribeModalClass]
      }
      footer={null}
      {...modalProps}>
      {derived.showViewer ? (
        <SVFViewer
          fileToView={fileToView}
          onClose={() => onCancel?.({} as any)}
          showTitle={false}
          onViewablesChange={setHeaderViewables}
          externalGuid={activeGuid}
        />
      ) : derived.showSuccess ? (
        <Fragment>
          <SuccessView
            onOpenWindowed={onOpenWindowed}
            onOpenFullscreen={onOpenFullscreen}
            onClose={handleModalCancel}
          />
        </Fragment>
      ) : derived.showProgress ? (
        <TranscribeProgress
          fileToView={fileToView}
          onClose={handleModalCancel}
          onSuccess={() => {
            //
          }}
          justTranscribed={justTranscribed}
          setJustTranscribed={setJustTranscribed}
          onStateChange={handleLocalSVFStateChange}
          onAttachmentStateChange={handleAttachmentBridgeStateChange}
        />
      ) : (
        <Transcribe
          fileToView={fileToView}
          onClose={handleModalCancel}
          onSuccess={() => {
            //
          }}
          justTranscribed={justTranscribed}
          setJustTranscribed={setJustTranscribed}
          onStateChange={handleLocalSVFStateChange}
          onAttachmentStateChange={handleAttachmentBridgeStateChange}
        />
      )}
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  fullModalClassName: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    margin: 0,
    top: 0,
    padding: '0 !important',
    maxWidth: '100%',
    '.ant-modal-content': {
      height: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    '.ant-modal-body': {
      flex: 1,
      padding: 0,
    },
  },
  transcribeModalClass: {
    display: 'flex',
    maxWidth: '100%',
    '.ant-modal-content': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    userSelect: 'none',
    gap: '20px',
    zIndex: 1,
  },
  infoMessage: {
    fontSize: 24,
    width: '100%',
    textAlign: 'center',
    paddingTop: '20px',
  },
  messageContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexFlow: 'column',
    position: 'relative',
  },
  note: {
    paddingTop: '10px',
    fontSize: 13,
    textAlign: 'center',
    maxWidth: 420,
    opacity: 0.85,
  },
  stages: {
    paddingBottom: '30px',
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
  },
  activeStageLabel: {
    fontWeight: 'bold',
    color: '#1890ff',
  },
  successView: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    height: '100%',
  },
  successTitle: {
    fontSize: 20,
    textAlign: 'center',
  },
  successActionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    maxWidth: 230,
  },
  errorMessage: {
    color: theme.colors.red,
    fontSize: 24,
    textAlign: 'center',
    border: `1px solid ${theme.colors.red}`,
    backgroundColor: theme.colors.white,
    borderRadius: 9,
    padding: '20px 20px',
    boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
  },
  pulsingMessage: {
    fontSize: 24,
    width: '100%',
    textAlign: 'center',
    paddingTop: '20px',
    animation: 'pulse 2s ease-in-out infinite',
    '@keyframes pulse': {
      '0%': {
        opacity: 0.6,
      },
      '50%': {
        opacity: 1,
      },
      '100%': {
        opacity: 0.6,
      },
    },
    '@keyframes spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
  },
});
